import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import type { Backtest } from "@/api/types";
import { isLabInstance, isLiveInstance } from "@/lib/live";
import { useUiStore } from "@/stores/ui";
import { Card } from "@/components/ui/kit";

type Step = { title: string; hint: string; done: boolean; cta: string; to?: string; onClick?: () => void };

/**
 * The path from first backtest to live trading (design `.journey`).
 * Backtesting comes first because it needs no exchange; the bar hides once
 * every step is done.
 */
export function Journey({ accounts, backtests }: { accounts: { id: string }[]; backtests: Backtest[] }) {
  const openNewBacktest = useUiStore((s) => s.openNewBacktest);
  const steps: Step[] = [
    {
      title: "Backtest a strategy",
      hint: "Paste Pine Script. No exchange needed.",
      done: backtests.some(isLabInstance),
      cta: "New backtest",
      onClick: () => openNewBacktest(),
    },
    {
      title: "Add an exchange account",
      hint: "Only when you're ready to trade.",
      done: accounts.length > 0,
      cta: "New account",
      to: "/integrations/marketplaces/new",
    },
    {
      title: "Go live",
      hint: "Run a backtest on your exchange.",
      done: backtests.some(isLiveInstance),
      cta: "Go live",
      to: "/live/new",
    },
  ];
  if (steps.every((s) => s.done)) return null;
  const current = steps.findIndex((s) => !s.done);

  return (
    <Card>
      <ol className="m-0 grid list-none grid-cols-1 p-0 sm:grid-cols-3">
        {steps.map((s, i) => {
          const now = i === current;
          return (
            <li
              key={s.title}
              className={`flex min-w-0 items-start gap-2.5 px-3.5 py-3 ${
                i > 0 ? "border-t border-[var(--divider)] sm:border-l sm:border-t-0" : ""
              } ${!now && !s.done ? "opacity-60" : ""}`}
            >
              <span
                className={`num grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
                  s.done
                    ? "bg-[var(--success-soft)] text-[var(--success)]"
                    : now
                      ? "bg-[var(--primary)] text-[var(--on-primary)]"
                      : "bg-[var(--bg-hover)] text-[var(--text-muted)]"
                }`}
              >
                {s.done ? <Check size={12} strokeWidth={3} /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <b className={`block text-[13px] font-semibold ${s.done ? "text-[var(--text-muted)] line-through" : "text-[var(--text-primary)]"}`}>
                  {s.title}
                </b>
                <span className="block text-xs text-[var(--text-muted)]">{s.hint}</span>
              </div>
              {now &&
                (s.to ? (
                  <Link to={s.to} className="btn btn-soft btn-sm">
                    {s.cta}
                  </Link>
                ) : (
                  <button type="button" className="btn btn-soft btn-sm" onClick={s.onClick}>
                    {s.cta}
                  </button>
                ))}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
